import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put, v1Post, v1Patch, v1Delete } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import RoleMultiSelect from '@/components/RoleMultiSelect';
import { formatMasterOptionLabel } from '@/lib/masterDataLabels';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Save, History, Workflow, MessageSquare, Handshake, ListChecks, Video, Trash2, ClipboardCheck } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Gated } from '@/auth/permissions';
import { IQ_ASSESSMENT_LABEL } from '@/lib/stageLabels';
import {
  STAGE_CATALOGUE,
  adaptPipeline,
  adaptStagesFromV1,
  canAddStage,
  formatApiError,
  formatVersionTimestamp,
  stageCatalogueLabel,
  toV1Stages,
} from './pipelines/pipelineUtils';
import { StageRow } from './pipelines/PipelineStageEditor';
import { VideoBotBinding, McqBinding, TechAssessmentBinding } from './pipelines/pipelineAssessmentBindings';
import { MultiPanelBinding, OfferBinding } from './pipelines/pipelinePanelOfferBindings';

export default function PipelinesSettings() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: rawPipelines } = useQuery({ queryKey: ['v1-pipelines'], queryFn: () => v1Get('/pipelines') });
  const pipelines = React.useMemo(() => (rawPipelines || []).map(adaptPipeline), [rawPipelines]);
  const { data: roles } = useQuery({ queryKey: ['v1-master-roles'], queryFn: () => v1Get('/master/roles') });
  const { data: depts } = useQuery({ queryKey: ['v1-master-departments'], queryFn: () => v1Get('/master/departments') });
  const [selectedKey, setSelectedKey] = useState(null);
  const [stages, setStages] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({ roleIds: [], department: '', label: '' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState(null);

  const onSelect = React.useCallback((key) => {
    const p = pipelines.find((x) => x.key === key);
    if (!p) return;
    setSelectedKey(key);
    setStages(p.stages || []);
    const next = new URLSearchParams(searchParams);
    next.set('pipeline', key);
    setSearchParams(next, { replace: true });
  }, [pipelines, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pipelines?.length) return;
    const urlKey = searchParams.get('pipeline');
    const match = urlKey ? pipelines.find((p) => p.key === urlKey) : null;
    const key = match?.key ?? pipelines[0].key;
    const p = pipelines.find((x) => x.key === key);
    setSelectedKey(key);
    setStages(p?.stages || []);
    if (urlKey !== key) {
      const next = new URLSearchParams(searchParams);
      next.set('pipeline', key);
      setSearchParams(next, { replace: true });
    }
  }, [pipelines, searchParams, setSearchParams]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = Number(active.id.split('-')[1]);
    const newIdx = Number(over.id.split('-')[1]);
    // Lock first (RESUME) and last (OFFER if exists)
    const hasOffer = stages[stages.length - 1]?.code === 'OFFER';
    const min = 1; const max = hasOffer ? stages.length - 2 : stages.length - 1;
    if (oldIdx < min || oldIdx > max || newIdx < min || newIdx > max) return;
    setStages(arrayMove(stages, oldIdx, newIdx));
  };

  const addStage = (code) => {
    if (!canAddStage(code, stages)) return;
    const hasOffer = stages[stages.length - 1]?.code === 'OFFER';
    const newStage = {
      code,
      label: code === 'PANEL'
        ? `Technical Panel ${stages.filter((s) => s.code === 'PANEL').length + 1}`
        : stageCatalogueLabel(code),
      code_editor: code === 'PANEL' || code === 'CODE_PANEL',
    };
    if (code === 'OFFER') { setStages([...stages.filter((s) => s.code !== 'OFFER'), newStage]); return; }
    const idx = hasOffer ? stages.length - 1 : stages.length;
    setStages([...stages.slice(0, idx), newStage, ...stages.slice(idx)]);
  };

  const selected = (pipelines || []).find((p) => p.key === selectedKey);
  const stagesDirty = React.useMemo(() => {
    if (!selected?.stages) return false;
    return JSON.stringify(toV1Stages(stages)) !== JSON.stringify(toV1Stages(selected.stages));
  }, [stages, selected?.stages]);

  const save = async () => {
    const pipe = (pipelines || []).find((p) => p.key === selectedKey);
    if (!pipe) return;
    if (!stagesDirty) return;
    if (stages.length < 2) {
      toast.error('A pipeline must include at least two stages (Resume plus one other).');
      return;
    }
    if (stages.some((s) => !(s.label || '').trim())) {
      toast.error('Every stage needs a label before saving.');
      return;
    }
    try {
      const updated = await v1Put(`/pipelines/${pipe._id}/stages`, { stages: toV1Stages(stages) });
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      await qc.invalidateQueries({ queryKey: ['v1-pipeline-versions', pipe._id] });
      if (updated?.current_version?.stages) {
        setStages(adaptStagesFromV1(updated.current_version.stages));
      }
      toast.success(`${pipe.label} pipeline saved — in-flight candidates keep their prior version.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const submitDelete = async () => {
    const pipe = (pipelines || []).find((p) => p.key === selectedKey);
    if (!pipe) return;
    setDeleting(true);
    try {
      await v1Delete(`/pipelines/${pipe._id}`);
      const remaining = (pipelines || []).filter((p) => p.key !== selectedKey);
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      if (remaining.length) {
        onSelect(remaining[0].key);
      } else {
        setSelectedKey(null);
        setStages([]);
        const next = new URLSearchParams(searchParams);
        next.delete('pipeline');
        setSearchParams(next, { replace: true });
      }
      toast.success(`Pipeline "${pipe.label}" deleted.`);
      setDeleteOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const { data: versionHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['v1-pipeline-versions', selected?._id],
    queryFn: () => v1Get(`/pipelines/${selected._id}/versions`),
    enabled: historyOpen && !!selected?._id,
    staleTime: 30_000,
  });

  const restoreVersion = async (versionRow) => {
    if (!selected?._id || !versionRow?.id) return;
    if (!versionRow?.stages?.length) {
      toast.error('That version has no stages to restore.');
      return;
    }
    setRestoringVersionId(versionRow.id);
    try {
      const updated = await v1Post(`/pipelines/${selected._id}/versions/${versionRow.id}/restore`, {});
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      await qc.invalidateQueries({ queryKey: ['v1-pipeline-versions', selected._id] });
      setStages(adaptStagesFromV1(updated?.current_version?.stages || versionRow.stages));
      setHistoryOpen(false);
      toast.success(`Restored v${versionRow.version} as the current pipeline version.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Could not restore pipeline version'));
    } finally {
      setRestoringVersionId(null);
    }
  };

  const roleName = (id) => (roles || []).find((r) => r.id === id)?.name || id || '—';
  const deptName = (id) => (depts || []).find((d) => d.id === id)?.name || id || '—';
  const rolesLabel = (p) => {
    if (!p) return '—';
    if (p.roleNames?.length) return p.roleNames.join(', ');
    if (p.roles?.length) return p.roles.map(roleName).join(', ');
    return roleName(p.role);
  };

  const savePipelineMeta = async (patch) => {
    if (!selected?._id || metaSaving) return;
    setMetaSaving(true);
    try {
      await v1Patch(`/pipelines/${selected._id}`, patch);
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      toast.success('Pipeline details saved');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save pipeline details');
    } finally {
      setMetaSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Pipelines" subtitle="Compose stage workflows per Role-Department. Resume must be first; Offer if present is last." actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setHistoryOpen(true)} data-testid="pipeline-history-button"><History className="h-4 w-4" /> History</Button>
          <Gated need="pipeline.edit">
            <Button variant="outline" className="gap-1.5" onClick={() => setNewOpen(true)} data-testid="pipeline-new-button"><Plus className="h-4 w-4" /> New pipeline</Button>
          </Gated>
          {selected && (
            <Gated need="pipeline.edit">
              <Button
                variant="outline"
                className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setDeleteOpen(true)}
                data-testid="pipeline-delete-button"
              >
                <Trash2 className="h-4 w-4" /> Delete pipeline
              </Button>
            </Gated>
          )}
          <Gated need="pipeline.edit">
            <Button onClick={save} disabled={!stagesDirty || !selected} className="gap-1.5" data-testid="pipeline-save-button"><Save className="h-4 w-4" /> Save version</Button>
          </Gated>
        </div>
      } />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
          <p className="shrink-0 px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipelines</p>
          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">{(pipelines || []).map((p) => (
            <li key={p.key}><button onClick={() => onSelect(p.key)} data-testid={`pipeline-select-${p.key}`} className={`flex w-full flex-col items-start rounded-lg px-3 py-2 text-left text-sm transition-colors ${selectedKey === p.key ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}>
              <span className="font-medium">{p.label}</span>
              <span className="text-[11px] text-muted-foreground">v{p.version} · {p.stages.length} stages</span>
            </button></li>
          ))}</ul>
        </CardContent></Card>

        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="min-h-0 flex-1 overflow-y-auto p-5" data-testid="pipeline-configurator">
          {selected ? (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold">{selected.label}</p>
                  <p className="text-xs text-muted-foreground">
                    Version {selected.version} · {rolesLabel(selected)} / {deptName(selected.department)}
                  </p>
                </div>
              </div>
              <Gated need="pipeline.edit">
                <div className="mt-3 grid grid-cols-1 gap-3 rounded-xl border bg-secondary/20 p-3 md:grid-cols-2" data-testid="pipeline-meta-editor">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Roles</label>
                    <RoleMultiSelect
                      roles={roles || []}
                      value={selected.roles || []}
                      onChange={(roleIds) => {
                        if (!roleIds.length) return toast.error('At least one role is required');
                        savePipelineMeta({ role_ids: roleIds });
                      }}
                      testId="pipeline-edit-roles"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Department</label>
                    <Select
                      value={selected.department || ''}
                      onValueChange={(v) => savePipelineMeta({ department_id: v })}
                      disabled={metaSaving}
                    >
                      <SelectTrigger data-testid="pipeline-edit-department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {(depts || []).map((d) => (
                          <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Gated>
              {/* Sprint 11.6 (#10) — Tabbed configuration. One tab per stage
                  present in the pipeline + an always-present "Stages" tab to
                  manage the ordered list itself. Eliminates the previous long
                  vertical scroll on pipelines with 5+ stages. */}
              <Tabs defaultValue="stages" className="mt-4">
                <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-secondary/60" data-testid="pipeline-config-tabs">
                  <TabsTrigger value="stages" data-testid="pipeline-tab-stages"><Workflow className="mr-1 h-3.5 w-3.5" /> Stages</TabsTrigger>
                  {stages.some((s) => s.code === 'VIDEOBOT') && (
                    <TabsTrigger value="videobot" data-testid="pipeline-tab-videobot"><Video className="mr-1 h-3.5 w-3.5" /> Video Bot</TabsTrigger>
                  )}
                  {stages.some((s) => s.code === 'MCQ') && (
                    <TabsTrigger value="mcq" data-testid="pipeline-tab-mcq"><ListChecks className="mr-1 h-3.5 w-3.5" /> IQ Assessment</TabsTrigger>
                  )}
                  {stages.some((s) => s.code === 'TECH_ASSESSMENT') && (
                    <TabsTrigger value="tech-assessment" data-testid="pipeline-tab-tech-assessment"><ClipboardCheck className="mr-1 h-3.5 w-3.5" /> Tech Assessment</TabsTrigger>
                  )}
                  {stages.some((s) => s.code === 'PANEL' || s.code === 'CODE_PANEL') && (
                    <TabsTrigger value="panel" data-testid="pipeline-tab-panel"><MessageSquare className="mr-1 h-3.5 w-3.5" /> Tech Panel</TabsTrigger>
                  )}
                  {stages.some((s) => s.code === 'OFFER') && (
                    <TabsTrigger value="offer" data-testid="pipeline-tab-offer"><Handshake className="mr-1 h-3.5 w-3.5" /> Offer</TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="stages" className="space-y-4">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={stages.map((_, i) => `s-${i}`)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {stages.map((s, i) => {
                          const locked = i === 0 || (s.code === 'OFFER');
                          return <StageRow key={`${s.code}-${i}`} stage={s} index={i} onChange={(updated) => { const next = [...stages]; next[i] = updated; setStages(next); }} onDelete={() => setStages(stages.filter((_, idx) => idx !== i))} locked={locked} />;
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <div className="rounded-xl border bg-secondary/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add stage</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {STAGE_CATALOGUE.filter((s) => s.code !== 'RESUME').map((s) => {
                        const eligible = canAddStage(s.code, stages);
                        return (
                          <Button
                            key={s.code}
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={!eligible}
                            onClick={() => addStage(s.code)}
                            data-testid={`pipeline-add-stage-${s.code}`}
                            title={eligible ? undefined : `Maximum ${s.instances} already in this pipeline`}
                          >
                            <Plus className="h-3.5 w-3.5" /> {s.label} <span className="text-[10px] text-muted-foreground">{s.instances}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                {selected._id && stages.some((s) => s.code === 'VIDEOBOT') && (
                  <TabsContent value="videobot">
                    <VideoBotBinding pipelineId={selected._id} pipelineLabel={selected.label} />
                  </TabsContent>
                )}
                {selected._id && stages.some((s) => s.code === 'MCQ') && (
                  <TabsContent value="mcq">
                    <McqBinding pipelineId={selected._id} pipelineLabel={selected.label} />
                  </TabsContent>
                )}
                {selected._id && stages.some((s) => s.code === 'TECH_ASSESSMENT') && (
                  <TabsContent value="tech-assessment">
                    <TechAssessmentBinding
                      pipelineId={selected._id}
                      pipelineLabel={selected.label}
                      roleIds={selected.roles || []}
                      departmentId={selected.department}
                    />
                  </TabsContent>
                )}
                {selected._id && stages.some((s) => s.code === 'PANEL' || s.code === 'CODE_PANEL') && (
                  <TabsContent value="panel">
                    <MultiPanelBinding
                      pipelineId={selected._id}
                      pipelineLabel={selected.label}
                      departmentId={selected.department}
                      panelStages={stages.filter((s) => s.code === 'PANEL' || s.code === 'CODE_PANEL')}
                    />
                  </TabsContent>
                )}
                {selected._id && stages.some((s) => s.code === 'OFFER') && (
                  <TabsContent value="offer">
                    <OfferBinding pipelineId={selected._id} pipelineLabel={selected.label} />
                  </TabsContent>
                )}
              </Tabs>
            </>
          ) : <p className="text-sm text-muted-foreground">Select a pipeline to configure.</p>}
        </CardContent></Card>
      </div>

      {/* New pipeline dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={(v) => { if (!deleting) setDeleteOpen(v); }}>
        <AlertDialogContent data-testid="delete-pipeline-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pipeline?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{selected?.label}</span> will be removed
              from the Pipelines list and will no longer be available for new candidates or evaluations.
              Existing candidates keep their pinned pipeline version. This action is recorded in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-pipeline-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitDelete(); }}
              disabled={deleting}
              className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
              data-testid="delete-pipeline-confirm"
            >
              {deleting ? 'Deleting…' : 'Delete pipeline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-pipeline-dialog">
          <DialogHeader>
            <DialogTitle>Create new pipeline</DialogTitle>
            <DialogDescription>A new pipeline starts with the default 5-stage workflow. You can drag, add, and remove stages after creation.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Pipeline label</label>
              <Input value={newForm.label} onChange={(e) => setNewForm({ ...newForm, label: e.target.value })} placeholder="e.g. Backend Engineer (Senior)" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Roles</label>
                <RoleMultiSelect
                  roles={roles || []}
                  value={newForm.roleIds}
                  onChange={(roleIds) => setNewForm({ ...newForm, roleIds })}
                  testId="new-pipeline-roles"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Department</label>
                <Select value={newForm.department} onValueChange={(v) => setNewForm({ ...newForm, department: v })}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>{(depts || []).map((d) => <SelectItem key={d.id} value={d.id}>{formatMasterOptionLabel(d)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-xl border bg-secondary/30 p-3 text-xs text-muted-foreground">
              <p className="inline-flex items-center gap-1.5 font-semibold text-foreground"><Workflow className="h-3.5 w-3.5 text-primary" /> Default stages</p>
              <p className="mt-1">Resume → Video Bot → IQ Assessment → Technical Panel 1 → Offer</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button data-testid="new-pipeline-confirm" onClick={async () => {
              if (!newForm.label) return toast.error('Pipeline label is required');
              if (!newForm.roleIds?.length) return toast.error('At least one role is required');
              if (!newForm.department) return toast.error('Department is required');
              // pipeline_key must stay readable and <= 64 chars, so derive it from
              // the primary role/department CODES (not the selected UUIDs).
              const primaryRoleId = newForm.roleIds[0];
              const roleCode = (roles || []).find((r) => r.id === primaryRoleId)?.code || 'ROLE';
              const deptCode = (depts || []).find((d) => d.id === newForm.department)?.code || 'DEPT';
              const key = `${roleCode}_${deptCode}_${Date.now().toString(36).slice(-4)}`;
              const defaultStages = [
                { code: 'RESUME',   label: 'Resume' },
                { code: 'VIDEOBOT', label: 'Video Bot' },
                { code: 'MCQ',      label: IQ_ASSESSMENT_LABEL },
                { code: 'PANEL',    label: 'Technical Panel 1', code_editor: true },
                { code: 'OFFER',    label: 'Offer' },
              ];
              try {
                await v1Post('/pipelines', {
                  pipeline_key: key,
                  label: newForm.label,
                  role_ids: newForm.roleIds,
                  department_id: newForm.department,
                  stages: toV1Stages(defaultStages),
                });
                qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
                toast.success(`Pipeline “${newForm.label}” created.`);
                setNewOpen(false);
                setNewForm({ roleIds: [], department: '', label: '' });
              } catch (e) {
                const detail = e?.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'Could not create pipeline');
              }
            }}>Create pipeline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] gap-0 p-0 overflow-hidden flex flex-col" data-testid="pipeline-history-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>Pipeline version history</DialogTitle>
            <DialogDescription>
              Every save creates a new immutable version. In-flight candidates continue on the version active when they entered the pipeline.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">No pipeline selected.</p>
            ) : historyLoading ? (
              <p className="text-sm text-muted-foreground" data-testid="pipeline-history-loading">Loading version history…</p>
            ) : !(versionHistory || []).length ? (
              <p className="text-sm text-muted-foreground" data-testid="pipeline-history-empty">
                No versions yet. Save the pipeline once to create v1.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="pipeline-history-list">
                {(versionHistory || []).map((v) => {
                  const stageCount = (v.stages || []).length;
                  const isCurrent = v.is_current === true;
                  return (
                    <li
                      key={v.id}
                      className="flex items-center justify-between rounded-xl border bg-card p-3 text-sm"
                      data-testid={`pipeline-history-row-v${v.version}`}
                    >
                      <div>
                        <p className="font-semibold">
                          v{v.version}
                          {isCurrent && (
                            <span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                              current
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isCurrent
                            ? `Active · ${formatVersionTimestamp(v.created_at)} · ${stageCount} stage${stageCount === 1 ? '' : 's'}`
                            : `Archived · ${formatVersionTimestamp(v.created_at)} · ${stageCount} stage${stageCount === 1 ? '' : 's'} snapshot`}
                        </p>
                      </div>
                      {isCurrent ? (
                        <span className="text-xs text-muted-foreground">Current</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={restoringVersionId === v.id}
                          onClick={() => restoreVersion(v)}
                          data-testid={`pipeline-history-restore-v${v.version}`}
                        >
                          {restoringVersionId === v.id ? 'Restoring…' : 'Restore'}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="ghost" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
